/**
 * CalendarCell - A single employee/day cell in the scheduling grid.
 *
 * Renders the shifts assigned that day, handles the drop target for worksite
 * drag&drop, and paints a striped vacation state. All state is passed in;
 * assignment and deletion are delegated to the orchestrator via callbacks.
 */
import type { Employee, PlannedShift } from './types';

interface CellProps {
  employee: Employee;
  date: string;
  shifts: PlannedShift[];
  isDraggingOver: boolean;
  isVacation: boolean;
  onDrop: (employeeId: number, date: string) => void;
  onDeleteShift: (shiftId: number) => void;
}

export function CalendarCell({
  employee,
  date,
  shifts,
  isDraggingOver,
  isVacation,
  onDrop,
  onDeleteShift,
}: CellProps) {
  const handleDragOver = (e: React.DragEvent) => {
    if (!isVacation) {
      e.preventDefault();
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    if (!isVacation) {
      onDrop(employee.id, date);
    }
  };

  const worksiteColors: Record<number, string> = {};
  const colorPalette = [
    'bg-info-soft text-brand border-info',
    'bg-brand-soft text-brand border-line-strong',
    'bg-purple-100 text-purple-800 border-purple-200',
    'bg-signal-soft text-signal border-signal',
    'bg-pink-100 text-pink-800 border-pink-200',
  ];

  const getWorksiteColor = (worksiteId: number) => {
    if (!worksiteColors[worksiteId]) {
      worksiteColors[worksiteId] = colorPalette[Object.keys(worksiteColors).length % colorPalette.length];
    }
    return worksiteColors[worksiteId];
  };

  if (isVacation) {
    return (
      <div className="h-full min-h-[80px] bg-surface-2 bg-stripes p-1">
        <span className="text-xs text-ink-2">Vacaciones</span>
      </div>
    );
  }

  return (
    <div
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      className={`
        h-full min-h-[80px] p-1 transition-colors
        ${isDraggingOver ? 'bg-info-soft ring-2 ring-blue-300 ring-inset' : 'bg-white'}
      `}
    >
      {shifts.map((shift) => (
        <div
          key={shift.id}
          className={`
            p-1.5 mb-1 rounded border text-xs relative group
            ${getWorksiteColor(shift.worksite_id)}
          `}
        >
          <div className="font-medium truncate">{shift.worksite_code}</div>
          <div className="opacity-75">
            {shift.expected_start_time && shift.expected_end_time
              ? `${shift.expected_start_time.slice(0, 5)} - ${shift.expected_end_time.slice(0, 5)}`
              : `${shift.expected_hours} h`}
          </div>

          {/* Delete button */}
          <button
            onClick={() => onDeleteShift(shift.id)}
            className="absolute -top-1 -right-1 w-4 h-4 bg-danger text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity text-xs leading-none"
          >
            ×
          </button>
        </div>
      ))}

      {shifts.length === 0 && (
        <div className="h-full flex items-center justify-center text-ink-3 text-xs">
          {isDraggingOver ? 'Soltar aqui' : ''}
        </div>
      )}
    </div>
  );
}
